import { useEffect, useState } from "react";
import { Star, Users, Mail, ClipboardList, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { api, fmtErr } from "../../lib/api";

export default function GuestsTab({ restaurant }) {
  const [guests, setGuests] = useState([]);
  const [notes, setNotes] = useState({});
  const [lapsed, setLapsed] = useState([]);
  const [surveyData, setSurveyData] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [replies, setReplies] = useState({});
  const [days, setDays] = useState(60);
  const [sending, setSending] = useState(false);

  const load = () =>
    api.get(`/restaurants/${restaurant.id}/guests`)
      .then((r) => {
        setGuests(r.data);
        setNotes(Object.fromEntries(r.data.map((g) => [g.email, g.notes || ""])));
      })
      .catch(() => {});
  const loadExtras = (d = days) => {
    api.get(`/restaurants/${restaurant.id}/guests/lapsed`, { params: { days: d } }).then((r) => setLapsed(r.data)).catch(() => {});
    api.get(`/restaurants/${restaurant.id}/surveys`).then((r) => setSurveyData(r.data)).catch(() => {});
    api.get(`/restaurants/${restaurant.id}/reviews/manage`).then((r) => setReviews(r.data)).catch(() => {});
  };
  useEffect(() => { load(); loadExtras(); }, [restaurant.id]);

  const patchReview = async (uid, body) => {
    try {
      await api.patch(`/restaurants/${restaurant.id}/reviews/${uid}`, body);
      toast.success(body.visible !== undefined ? (body.visible ? "Review visible" : "Review hidden") : "Reply published");
      loadExtras();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const reengage = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/restaurants/${restaurant.id}/guests/reengage`, { days });
      toast.success(`Re-engagement email sent to ${data.sent} guest${data.sent === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(fmtErr(e));
    }
    setSending(false);
  };

  const patch = async (email, body) => {
    try {
      await api.patch(`/restaurants/${restaurant.id}/guests/${encodeURIComponent(email)}`, body);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  return (
    <div data-testid="guests-tab">
      <div className="flex items-center gap-2 mb-6 text-muted-foreground text-sm">
        <Users className="w-4 h-4 text-primary" />
        Guests earn 10 loyalty points per cover for completed visits. Mark regulars as VIP.
      </div>
      <div className="rounded-2xl border border-border/60 bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border/60">
              <TableHead>Guest</TableHead>
              <TableHead>Visits</TableHead>
              <TableHead>Covers</TableHead>
              <TableHead>Points</TableHead>
              <TableHead>No-shows</TableHead>
              <TableHead>Last visit</TableHead>
              <TableHead>VIP</TableHead>
              <TableHead>Notes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {guests.map((g) => (
              <TableRow key={g.email} data-testid={`guest-row-${g.email}`} className="border-border/40">
                <TableCell>
                  <p className="font-medium flex items-center gap-1.5">
                    {g.name}
                    {g.vip && <Star className="w-3.5 h-3.5 text-amber-300 fill-amber-300" />}
                  </p>
                  <p className="text-xs text-muted-foreground">{g.email}</p>
                </TableCell>
                <TableCell>{g.visits}</TableCell>
                <TableCell>{g.covers}</TableCell>
                <TableCell><Badge variant="outline" className="text-primary border-primary/40">{g.points} pts</Badge></TableCell>
                <TableCell>{g.no_shows}</TableCell>
                <TableCell className="text-sm">{g.last_visit}</TableCell>
                <TableCell>
                  <Switch data-testid={`vip-${g.email}`} checked={g.vip} onCheckedChange={(v) => patch(g.email, { vip: v })} />
                </TableCell>
                <TableCell>
                  <Input data-testid={`notes-${g.email}`} value={notes[g.email] ?? ""} placeholder="Add note..."
                    onChange={(e) => setNotes({ ...notes, [g.email]: e.target.value })}
                    onBlur={() => notes[g.email] !== g.notes && patch(g.email, { notes: notes[g.email] })}
                    className="bg-background border-border/60 h-8 w-44 text-xs" />
                </TableCell>
              </TableRow>
            ))}
            {guests.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-muted-foreground" data-testid="no-guests-msg">
                  No guests yet — they appear after their first booking.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        <div className="rounded-2xl border border-border/60 bg-card p-6" data-testid="lapsed-section">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h3 className="font-outfit font-medium text-lg flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" /> Lapsed diners
            </h3>
            <div className="flex items-center gap-2">
              <Input data-testid="lapsed-days-input" type="number" min="7" value={days}
                onChange={(e) => setDays(Number(e.target.value) || 60)}
                onBlur={() => loadExtras()}
                className="bg-background border-border/60 h-8 w-20 text-xs" />
              <span className="text-xs text-muted-foreground">days</span>
              <Button data-testid="reengage-btn" size="sm" className="rounded-full h-8" onClick={reengage}
                disabled={sending || lapsed.length === 0}>
                {sending ? "Sending..." : "Email all"}
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">Guests whose last visit was more than {days} days ago.</p>
          <div className="mt-4 space-y-2.5">
            {lapsed.map((g) => (
              <div key={g.email} data-testid={`lapsed-${g.email}`} className="flex items-center justify-between gap-3 rounded-xl border border-border/40 px-4 py-3">
                <div>
                  <p className="text-sm font-medium">{g.name}</p>
                  <p className="text-xs text-muted-foreground">{g.email}</p>
                </div>
                <p className="text-xs text-muted-foreground shrink-0">Last visit {g.last_visit} · {g.visits} visits</p>
              </div>
            ))}
            {lapsed.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-lapsed-msg">No lapsed diners in this window — nice retention.</p>}
          </div>
        </div>

        <div className="rounded-2xl border border-border/60 bg-card p-6" data-testid="surveys-section">
          <h3 className="font-outfit font-medium text-lg flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-primary" /> Guest surveys
            {surveyData?.avg_rating != null && (
              <Badge variant="outline" className="text-amber-300 border-amber-300/40 ml-1">{surveyData.avg_rating} avg · {surveyData.count}</Badge>
            )}
          </h3>
          <p className="text-xs text-muted-foreground mt-2">Post-visit surveys live at /survey/{"{booking-id}"} — share the link in follow-up emails.</p>
          <div className="mt-4 space-y-2.5">
            {(surveyData?.surveys || []).slice(0, 8).map((s) => (
              <div key={s.booking_id} data-testid={`survey-${s.booking_id}`} className="rounded-xl border border-border/40 px-4 py-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">{s.guest_name} <span className="text-muted-foreground font-normal">· {s.date}</span></p>
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`w-3.5 h-3.5 ${n <= s.rating ? "text-amber-300 fill-amber-300" : "text-muted-foreground"}`} />
                    ))}
                  </div>
                </div>
                {s.comment && <p className="text-xs text-muted-foreground mt-1.5">{s.comment}</p>}
              </div>
            ))}
            {(surveyData?.surveys || []).length === 0 && (
              <p className="text-sm text-muted-foreground" data-testid="no-surveys-msg">No survey responses yet.</p>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border/60 bg-card p-6 mt-6" data-testid="reviews-manage-section">
        <h3 className="font-outfit font-medium text-lg flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-primary" /> Reviews
        </h3>
        <p className="text-xs text-muted-foreground mt-2">Reply publicly to guest reviews, or hide one from your restaurant page.</p>
        <div className="mt-4 space-y-3">
          {reviews.map((rv) => (
            <div key={rv.user_id} data-testid={`review-row-${rv.user_id}`}
              className={`rounded-xl border px-4 py-3 ${rv.visible === false ? "border-border/30 opacity-60" : "border-border/40"}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium">{rv.name}</p>
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`w-3 h-3 ${n <= rv.rating ? "text-amber-300 fill-amber-300" : "text-muted-foreground"}`} />
                    ))}
                  </div>
                  {rv.visible === false && <Badge variant="outline" className="text-muted-foreground">hidden</Badge>}
                </div>
                <Button data-testid={`review-visibility-${rv.user_id}`} size="sm" variant="outline" className="rounded-full h-7 text-xs"
                  onClick={() => patchReview(rv.user_id, { visible: rv.visible === false })}>
                  {rv.visible === false ? "Show" : "Hide"}
                </Button>
              </div>
              {rv.comment && <p className="text-sm text-muted-foreground mt-1.5">{rv.comment}</p>}
              {rv.reply && <p className="text-xs text-primary mt-1.5">Your reply: {rv.reply}</p>}
              <div className="flex gap-2 mt-2.5">
                <Input data-testid={`review-reply-input-${rv.user_id}`} placeholder="Write a public reply..."
                  value={replies[rv.user_id] ?? (rv.reply || "")}
                  onChange={(e) => setReplies({ ...replies, [rv.user_id]: e.target.value })}
                  className="bg-background border-border/60 h-8 text-xs" />
                <Button data-testid={`review-reply-save-${rv.user_id}`} size="sm" variant="secondary" className="rounded-full h-8 text-xs shrink-0"
                  onClick={() => patchReview(rv.user_id, { reply: replies[rv.user_id] ?? "" })}>
                  Reply
                </Button>
              </div>
            </div>
          ))}
          {reviews.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-reviews-msg">No reviews yet — they appear once diners rate their visit.</p>}
        </div>
      </div>
    </div>
  );
}
